import { parseTime, formatTimeFromMinutes, timeToMinutes } from '../utils/helpers';
import AcademicYearService from '../services/AcademicYearService';
import Teacher, { RawTeacherData } from './Teacher';
import Room, { RawRoomData } from './Room';
import Subject, { RawSubjectData } from './Subject';
import { SCHEDULE_FILTER_ALL_GROUPS } from '../config/constants';

// Type definitions for teacher associations from backend
interface TeacherAssociation extends RawTeacherData {
  ScheduleEntryTeachers?: {
    teacher_order: number;
    role: 'primary' | 'secondary' | 'assistant' | 'guest';
  };
}

interface ScheduleEntryData {
  id?: string | number; // Allow both string and number from backend
  // Subject can come as a name, an object (from backend include), or an ID
  subject?: string | Subject | RawSubjectData | { id: number | string; name?: string };
  course?: string; // Legacy field for backward compatibility
  // Teacher can come as a name, an object (from backend include), or an ID
  teacher?: string | Teacher | RawTeacherData | { id: number; name?: string };
  // NEW: Multiple teachers array from backend
  teachers?: TeacherAssociation[];
  // Host field for events (instead of teacher)
  host?: string;
  groups?: (number | string | { id: number; name?: string; group_code?: string })[];
  // Room can come as a name, an object (from backend include), or an ID
  room?: string | Room | RawRoomData | { id: number; name?: string; room_code?: string } | number;
  day?: string;
  weekSeries?: string;
  year?: number;
  title?: string;
  eventName?: string;
  description?: string;
  location?: string;
  type?: string;
  time?: string;
  timeStart?: string;
  timeEnd?: string;
  notes?: string;
  exceptions?: any;
  periodId?: string | null;
  // Stable identity preserved across live/draft copies for publish deduplication
  publishIdentity?: string;
  // Prefer explicit IDs where available
  teacher_id?: number;
  subject_id?: number | string;
  room_id?: number;
  // Server-side annotation (e.g. 'existing_live' | 'from_draft')
  source?: string;
  // Updated conflict status properties for per-week conflicts
  conflictsByWeek?: Map<string, WeekConflictInfo>; // Key format: "week-year"
  conflictLastCalculated?: Date | null;
  [key: string]: any; // For additional properties
}

// Per-week conflict information
interface WeekConflictInfo {
  week: number;
  year: number;
  status: 'green' | 'orange' | 'red';
  reasons: Array<{ reason: string; severity: 'green' | 'orange' | 'red' }>;
  lastCalculated: Date;
}

// Normalized interface for internal use - no duplicate field names
interface NormalizedScheduleEntryData {
  id: string;
  subject: string;
  teacher: string; // Legacy single teacher (for backward compatibility)
  teachers: TeacherAssociation[]; // NEW: Multiple teachers array
  host: string; // Host for events
  groups: (number | string | { id: number; name?: string; group_code?: string })[];
  room: string;
  day: string;
  weekSeries: string;
  year: number;
  title: string;
  eventName: string;
  description: string;
  location: string;
  type: string;
  timeStart: string;
  timeEnd: string;
  notes: string;
  exceptions: ScheduleEntryException[] | null;
  periodId: string | null;
  publishIdentity: string;
  // Updated conflict status properties for per-week conflicts
  conflictsByWeek: Map<string, WeekConflictInfo>;
  conflictLastCalculated: Date | null;
  additionalProps: Record<string, any>;
}

const API_FIELD_NAMES = [
  'week_series',
  'entry_type',
  'start_time',
  'end_time',
  'publish_identity',
  'period_id',
  'subject_name',
  'teacher_name',
  'room_name',
  'group_ids',
];

function assertFrontendScheduleEntryData(data: ScheduleEntryData): void {
  const leakedApiFields = API_FIELD_NAMES.filter(field => Object.prototype.hasOwnProperty.call(data, field));
  if (leakedApiFields.length === 0) return;

  throw new Error(
    `[ScheduleEntry] API field names received by model constructor: ${leakedApiFields.join(', ')}. ` +
    'Use mapScheduleEntryFromApi before constructing ScheduleEntry.'
  );
}

// Utility function to normalize frontend field names and validate for conflicts
function normalizeScheduleEntryData(data: ScheduleEntryData): Partial<NormalizedScheduleEntryData> {
  const normalized: Partial<NormalizedScheduleEntryData> = {};

  // Only normalize fields that actually exist in the input data
  // This prevents empty string overrides in exceptions
  
  if (data.timeStart) {
    normalized.timeStart = data.timeStart;
  }
  
  if (data.timeEnd) {
    normalized.timeEnd = data.timeEnd;
  }
  
  if (data.weekSeries) {
    normalized.weekSeries = data.weekSeries;
  }
  
  if (data.type) {
    normalized.type = data.type;
  }
  
  // Handle other fields that might need normalization
  if (typeof data.teacher === 'string') {
    normalized.teacher = data.teacher;
  }
  
  // Preserve multi-teacher array when provided (e.g., in exception overrides)
  if (Array.isArray((data as any).teachers)) {
    (normalized as any).teachers = (data as any).teachers as any;
  }
  
  if (typeof data.room === 'string') {
    normalized.room = data.room;
  }
  
  if (data.day !== undefined) {
    normalized.day = data.day;
  }
  
  if (typeof data.subject === 'string') {
    normalized.subject = data.subject;
  }
  
  if (data.groups !== undefined) {
    normalized.groups = data.groups;
  }
  
  if (data.year !== undefined) {
    normalized.year = data.year;
  }
  
  if (data.title !== undefined) {
    normalized.title = data.title;
  }
  
  if (data.eventName !== undefined) {
    normalized.eventName = data.eventName;
  }
  
  if (data.description !== undefined) {
    normalized.description = data.description;
  }
  
  if (data.location !== undefined) {
    normalized.location = data.location;
  }
  
  if (data.notes !== undefined) {
    normalized.notes = data.notes;
  }
  
  if (data.exceptions !== undefined) {
    // Handle exceptions - can be array, null, or JSON string from backend
    let exceptionsValue = data.exceptions;
    
    // If it's a string, try to parse it as JSON
    if (typeof exceptionsValue === 'string' && exceptionsValue.trim() !== '') {
      try {
        exceptionsValue = JSON.parse(exceptionsValue);
      } catch (e) {
        console.warn('[ScheduleEntry] Failed to parse exceptions string:', e);
        exceptionsValue = null;
      }
    }
    
    // Ensure exceptions is always an array or null
    normalized.exceptions = Array.isArray(exceptionsValue) ? exceptionsValue : null;
  }
  
  if (data.periodId !== undefined) {
    normalized.periodId = data.periodId;
  }
  
  // Conflict status properties
  if (data.conflictsByWeek !== undefined) {
    normalized.conflictsByWeek = data.conflictsByWeek;
  }
  
  if (data.conflictLastCalculated !== undefined) {
    normalized.conflictLastCalculated = data.conflictLastCalculated;
  }
  
  if (data.host !== undefined) {
    normalized.host = data.host;
  }
  
  return normalized;
}

interface ParsedWeekRange {
  type: 'single' | 'range';
  week?: number;
  start?: number;
  end?: number;
  crossYear?: boolean;
}

interface TimeInfo {
  startMinutes: number | null;
  endMinutes: number | null;
  durationMinutes: number | null;
}

interface ScheduleEntryException {
  week: number;
  year: number;
  changes: Partial<ScheduleEntryData & { cancelled?: boolean }>;
}

/**
 * ScheduleEntry class for managing schedule entries data with accessors and converters
 */
class ScheduleEntry {
  public id: string;
  // Association objects as single source of truth (may be null if not loaded)
  public subjectObj: Partial<Subject> | null;
  public course: string; // Legacy field for backward compatibility
  public teacherObj: Partial<Teacher> | null; // Legacy single teacher (for backward compatibility)
  public teachersArray: TeacherAssociation[]; // NEW: Multiple teachers array with roles and order
  public host: string; // Host for events (instead of teacher)
  public groups: (number | string | { id: number; name?: string; group_code?: string })[];
  public roomObj: Partial<Room> | null;
  public day: string;
  public year: number;
  public title: string;
  public eventName: string;
  public description: string;
  public location: string;
  public type: string;
  public timeStart: string;
  public timeEnd: string;
  public notes: string;
  public exceptions: ScheduleEntryException[] | null;
  public periodId: string | null;
  /** Stable logical identity preserved across live/draft copies for publish deduplication. */
  public publishIdentity: string;
  public additionalProps: Record<string, any>;

  // No separate ID/name mirrors; derive IDs/names from the objects only

  // Updated conflict status properties for per-week conflicts
  public conflictsByWeek: Map<string, WeekConflictInfo>;
  public conflictLastCalculated: Date | null;

  private _weekSeries: string;
  private _parsedWeekRanges: ParsedWeekRange[] | null;

  constructor(data: ScheduleEntryData | ScheduleEntry = {}) {
    if (!(data instanceof ScheduleEntry)) {
      assertFrontendScheduleEntryData(data);
    }

    // First normalize the input data to resolve field name conflicts
    const normalizedData = data instanceof ScheduleEntry ? data.toObject() : normalizeScheduleEntryData(data);
    
    // Safely convert id to string, handling both string and number types
    this.id = data.id ? String(data.id) : '';
    
    // FIX: Handle both frontend field names and backend field names
    // Also handle backend associations which come as objects
    let subjectName = '';
    
    // Extract string value from subject field (object or string)
    if (data.subject) {
      if (typeof data.subject === 'string') {
        subjectName = data.subject;
      } else if (typeof data.subject === 'object' && data.subject !== null) {
        subjectName = (data.subject as any)?.name || (data.subject as any)?.subject_name || (data.subject as any)?.subject_code || '';
      }
    } else if ((data as any).subject_name) {
      subjectName = (data as any).subject_name;
    } else if (data.course) {
      if (typeof data.course === 'string') {
        subjectName = data.course;
      } else if (typeof data.course === 'object' && data.course !== null) {
        subjectName = (data.course as any)?.name || (data.course as any)?.course_name || (data.course as any)?.subject_name || (data.course as any)?.subject_code || '';
      }
    } else if ((data as any).course_name) {
      subjectName = (data as any).course_name;
    }
    
    // Initialize association objects only
    this.subjectObj = null;
    if (data.subject && typeof data.subject === 'object') {
      const subj: any = data.subject;
      this.subjectObj = {
        id: subj.id ?? (data as any).subject_id,
        name: subj.name ?? (data as any).subject_name,
        // Preserve subject color when provided by backend include
        color: subj.color ?? subj.subject_color
      } as any;
    } else if ((data as any).subject_id || (data as any).subject_name || subjectName) {
      this.subjectObj = {
        id: (data as any).subject_id,
        name: (data as any).subject_name ?? subjectName,
        // Some APIs may provide a flat subject_color field
        color: (data as any).subject_color
      } as any;
    }
    this.course = (this.subjectObj as any)?.name || ''; // Backward compatibility
    
    // Handle teacher field (string or object)
    let teacherName = '';
    if (data.teacher) {
      if (typeof data.teacher === 'string') {
        teacherName = data.teacher;
      } else if (typeof data.teacher === 'object' && data.teacher !== null) {
        teacherName = (data.teacher as any)?.name || (data.teacher as any)?.teacher_name || (data.teacher as any)?.full_name || '';
      }
    } else if ((data as any).teacher_name) {
      teacherName = (data as any).teacher_name;
    }
    
    // Teacher association (legacy single teacher)
    this.teacherObj = null;
    if (data.teacher && typeof data.teacher === 'object') {
      const tch: any = data.teacher;
      this.teacherObj = { id: tch.id ?? (data as any).teacher_id, name: tch.name ?? (data as any).teacher_name } as any;
    } else if ((data as any).teacher_id || (data as any).teacher_name || teacherName) {
      this.teacherObj = { id: (data as any).teacher_id, name: (data as any).teacher_name ?? teacherName } as any;
    }

    // Handle host field for events
    // For events, host info may come from teacher_name field in backend data
    this.host = normalizedData.host ?? data.host ?? '';
    
    // Multiple teachers array (NEW) - but not for events without teachers
    this.teachersArray = [];
    if (data.teachers && Array.isArray(data.teachers)) {
      this.teachersArray = data.teachers.map(teacher => ({
        ...teacher,
        // Ensure we have the association data
        ScheduleEntryTeachers: teacher.ScheduleEntryTeachers || {
          teacher_order: 1,
          role: 'primary' as const
        }
      }));
    } else if (this.teacherObj && this.teacherObj.name && this.teacherObj.name.trim()) {
      // Only create teachers array if we have a valid teacher name
      // This prevents creating invalid teacher objects with id: 0 for events
      this.teachersArray = [{
        id: (this.teacherObj as any).id || 0,
        institution_id: 0, // Will be set from context
        teacher_code: '',
        name: (this.teacherObj as any).name || '',
        email: '',
        ScheduleEntryTeachers: {
          teacher_order: 1,
          role: 'primary' as const
        }
      }];
    }
    
    // Handle groups field
    this.groups = normalizedData.groups ?? data.groups ?? [];
    
    // Handle room field (string or object) - ensure it's always a string
    let roomName = '';
    if (data.room !== null && data.room !== undefined) {
      if (typeof data.room === 'string') {
        roomName = data.room;
      } else if (typeof data.room === 'number') {
        roomName = String(data.room);
      } else if (typeof data.room === 'object') {
        roomName = (data.room as any)?.name || (data.room as any)?.room_name || (data.room as any)?.room_code || '';
      } else {
        roomName = String(data.room);
      }
    } else if ((data as any).room_name) {
      roomName = typeof (data as any).room_name === 'string' ? (data as any).room_name : String((data as any).room_name || '');
    }
    // Room association
    this.roomObj = null;
    if (data.room && typeof data.room === 'object') {
      const rm: any = data.room;
      this.roomObj = { id: rm.id ?? (data as any).room_id, name: rm.name ?? (data as any).room_name ?? rm.room_code, room_code: rm.room_code } as any;
    } else if ((data as any).room_id || (data as any).room_name || (data as any).room_code || roomName) {
      // Prefer the explicit room string provided in data.room (roomName) over legacy fields
      // This ensures moves that set room as a string are not overridden by stale room_name
      this.roomObj = {
        id: (data as any).room_id,
        name: roomName || (data as any).room_name || (data as any).room_code || '',
        room_code: (data as any).room_code
      } as any;
    }
    this.day = normalizedData.day ?? data.day ?? '';
    
    this._weekSeries = normalizedData.weekSeries ?? data.weekSeries ?? '';
    this.year = normalizedData.year ?? data.year ?? new Date().getFullYear();
    
    // Event-specific fields
    this.title = normalizedData.title ?? data.title ?? (data as any).eventName ?? '';
    this.eventName = normalizedData.eventName ?? (data as any).eventName ?? data.title ?? '';
    this.description = normalizedData.description ?? data.description ?? '';
    this.location = normalizedData.location ?? data.location ?? '';
    
    // Use normalized type to avoid conflicts, with fallback
    if (normalizedData.type) {
      this.type = normalizedData.type;
    } else if (data.type) {
      this.type = data.type;
    } else {
      this.type = (this.groups && this.groups.length > 0) ? 'class' : 'event';
    }
    
    // For events, handle host field mapping from backend teacher_name
    if (this.type === 'event') {
      // For events, host info comes from teacher_name field in backend
      if (!this.host && teacherName) {
        this.host = teacherName;
      }
      // Clear teacher fields for events since host is the primary field
      this.teacherObj = null;
      this.teachersArray = [];
    }
    
    // Use normalized time fields to avoid conflicts
    if (data instanceof ScheduleEntry) {
      this.timeStart = data.timeStart;
      this.timeEnd = data.timeEnd;
    } else {
      // Prioritize normalized time fields over legacy time field
      // This ensures exceptions with timeStart/timeEnd work correctly
      if (normalizedData.timeStart !== undefined || normalizedData.timeEnd !== undefined) {
        this.timeStart = normalizedData.timeStart ?? '';
        this.timeEnd = normalizedData.timeEnd ?? '';
      } else if (data.time) {
        // Fallback to parsing legacy time field only if no normalized fields exist
        const timeParts = data.time.split(' - ');
        if (timeParts.length === 2 && timeParts[0] && timeParts[1]) {
          this.timeStart = timeParts[0].trim();
          this.timeEnd = timeParts[1].trim();
        } else {
          this.timeStart = '';
          this.timeEnd = '';
        }
      } else {
        // Final fallback to direct field access - ensure strings
        const startTime = data.timeStart || '';
        const endTime = data.timeEnd || '';
        this.timeStart = typeof startTime === 'string' ? startTime : String(startTime || '');
        this.timeEnd = typeof endTime === 'string' ? endTime : String(endTime || '');
      }
    }
    
    this.notes = normalizedData.notes ?? data.notes ?? '';
    this.publishIdentity = (data as ScheduleEntryData).publishIdentity ?? '';
    
    // Handle exceptions - can be array, null, or JSON string from backend
    let exceptionsValue = normalizedData.exceptions ?? data.exceptions ?? null;
    
    // If it's a string, try to parse it as JSON (backend sometimes sends it as stringified JSON)
    if (typeof exceptionsValue === 'string' && exceptionsValue.trim() !== '') {
      try {
        exceptionsValue = JSON.parse(exceptionsValue);
      } catch (e) {
        console.warn('[ScheduleEntry] Failed to parse exceptions string:', e);
        exceptionsValue = null;
      }
    }
    
    // Ensure exceptions is always an array or null, never an object or other type
    this.exceptions = Array.isArray(exceptionsValue) ? exceptionsValue : null;
    this.periodId = normalizedData.periodId ?? (data as ScheduleEntryData).periodId ?? null;

    // Note: no separate ID caches; IDs can be read from subjectObj/teacherObj/roomObj
    
    // Parse any additional properties (excluding the known event fields)
    // First, handle any existing additionalProps from the input data
    const inputAdditionalProps = (data as any).additionalProps || {};
    this.additionalProps = { ...inputAdditionalProps };
    
    // Then add any other unknown fields to additionalProps
    Object.keys(data).forEach(key => {
      if (!['id', 'subject', 'course', 'type', 'teacher', 'teachers', 'host', 'groups', 'room', 'day', 'time', 'timeStart', 'timeEnd', 'notes', 'weekSeries', 'year', 'exceptions', 'periodId', 'title', 'eventName', 'description', 'location', 'conflictsByWeek', 'conflictLastCalculated', 'additionalProps', 'publishIdentity'].includes(key)) {
        this.additionalProps[key] = (data as any)[key];
      }
    });

    // Initialize week series parsing
    this._parsedWeekRanges = this._parseWeekSeries(this._weekSeries);

    // Updated conflict status properties for per-week conflicts
    this.conflictsByWeek = normalizedData.conflictsByWeek ?? data.conflictsByWeek ?? new Map();
    this.conflictLastCalculated = normalizedData.conflictLastCalculated ?? data.conflictLastCalculated ?? null;
  }

  // Read-only name accessors derived from association objects or denormalized fields
  get subject(): string {
    if (this.subjectObj && (this.subjectObj as any).name) return (this.subjectObj as any).name as string;
    return '';
  }

  get teacher(): string {
    if (this.teacherObj && (this.teacherObj as any).name) return (this.teacherObj as any).name as string;
    return '';
  }

  // NEW: Multiple teachers getters
  get teachers(): TeacherAssociation[] {
    return this.teachersArray || [];
  }

  get teachersFormatted(): string {
    if (!this.teachersArray || this.teachersArray.length === 0) {
      return '';
    }
    
    // Sort by teacher_order and format names with roles
    const sortedTeachers = [...this.teachersArray].sort((a, b) => {
      const orderA = a.ScheduleEntryTeachers?.teacher_order || 1;
      const orderB = b.ScheduleEntryTeachers?.teacher_order || 1;
      return orderA - orderB;
    });
    
    return sortedTeachers.map(teacher => teacher.name).join(', ');
  }

  /**
   * Checks whether this entry involves the given teacher name.
   * Matches against legacy single teacher and the multi-teacher association list.
   * Comparison is case-insensitive and requires full-name equality.
   */
  hasTeacher(teacherName: string): boolean {
    if (!teacherName || typeof teacherName !== 'string') return false;
    const needle = teacherName.trim().toLowerCase();

    // Legacy single teacher string
    if (this.teacher && this.teacher.trim().toLowerCase() === needle) {
      return true;
    }

    // Multi-teacher array
    if (Array.isArray(this.teachersArray) && this.teachersArray.length > 0) {
      for (const t of this.teachersArray) {
        const name = (t && (t as any).name) ? String((t as any).name) : '';
        if (name && name.trim().toLowerCase() === needle) {
          return true;
        }
      }
    }

    return false;
  }

  get primaryTeacher(): TeacherAssociation | null {
    if (!this.teachersArray || this.teachersArray.length === 0) {
      return null;
    }
    
    // Find the primary teacher or return the first one
    return this.teachersArray.find(teacher => 
      teacher.ScheduleEntryTeachers?.role === 'primary'
    ) || this.teachersArray[0] || null;
  }

  get room(): string {
    if (this.roomObj && (this.roomObj as any).name) return (this.roomObj as any).name as string;
    if (this.roomObj && (this.roomObj as any).room_code) return (this.roomObj as any).room_code as string;
    return '';
  }

  /** Server-side annotation attached to preview entries (e.g. 'existing_live' | 'from_draft'). */
  get source(): string | undefined {
    return this.additionalProps?.source as string | undefined;
  }

  get groupsFormatted(): string {
    if (!this.groups || !Array.isArray(this.groups) || this.groups.length === 0) {
      return '';
    }
    
    return this.groups.map(group => {
      // Handle different group formats (object with name, or just name string)
      if (typeof group === 'object' && group.name) {
        return group.name;
      } else if (typeof group === 'string') {
        return group;
      } else if (typeof group === 'object' && group.group_code) {
        return group.group_code;
      }
      return '';
    }).filter(name => name).join(', ');
  }

  /**
   * Pre-parses the week series string into an optimized data structure for faster lookups.
   * @param {string} weekSeries - The week series string (e.g., "1-5,10,15-20")
   * @returns {Array} - Array of parsed week range objects
   * @private
   */
  private _parseWeekSeries(weekSeries: string): ParsedWeekRange[] | null {
    // If no week series is specified, return null (means active for all weeks)
    if (!weekSeries || weekSeries.trim() === '') {
      return null;
    }

    const ranges: ParsedWeekRange[] = [];
    const weekRanges = weekSeries.split(',').map(s => s.trim());
    
    for (const range of weekRanges) {
      if (range.includes('-')) {
        const rangeParts = range.split('-');
        if (rangeParts.length === 2 && rangeParts[0] && rangeParts[1]) {
          const start = Number(rangeParts[0]);
          const end = Number(rangeParts[1]);
          
          // Skip if parsing failed
          if (isNaN(start) || isNaN(end)) continue;
          
          ranges.push({
            type: 'range',
            start: start,
            end: end,
            crossYear: start > end // Cross-year range like "52-4"
          });
        }
      } else {
        const weekNumber = parseInt(range, 10);
        
        // Skip if parsing failed
        if (isNaN(weekNumber)) continue;
        
        ranges.push({
          type: 'single',
          week: weekNumber
        });
      }
    }
    
    return ranges.length > 0 ? ranges : null;
  }

  get isTimeValid(): boolean {
    return !!(this.timeStart && this.timeEnd);
  }

  // Legacy getter for compatibility
  get time(): never { 
    throw new Error('The time getter is deprecated. Please use timeStart and timeEnd properties instead.');
  }

  shouldBeInCalendar(): boolean {
    return !!(this.timeStart && this.timeEnd && this.room && this.day);
  }

  /**
   * Check if this entry is scheduled (has all fields required to be placed on the calendar)
   * An entry is considered scheduled if it has:
   * - timeStart (valid non-empty string)
   * - timeEnd (valid non-empty string) 
   * - room (valid non-empty string)
   * - day (valid non-empty string)
   * - weekSeries (valid non-empty string)
   */
  isScheduled(): boolean {
    return !!(
      this.timeStart && 
      typeof this.timeStart === 'string' && 
      this.timeStart.trim() && 
      this.timeEnd && 
      typeof this.timeEnd === 'string' && 
      this.timeEnd.trim() && 
      this.room && 
      typeof this.room === 'string' && 
      this.room.trim() &&
      this.day && 
      typeof this.day === 'string' && 
      this.day.trim() &&
      this.weekSeries && 
      typeof this.weekSeries === 'string' && 
      this.weekSeries.trim()
    );
  }

  /**
   * Unschedule this entry (remove time and room info)
   */
  unschedule(): ScheduleEntry {
    return new ScheduleEntry({
      ...this.toObject(), // Use toObject() to get clean data instead of spreading this
      day: '',
      timeStart: '',
      timeEnd: '',
      room: '',
      exceptions: null, // Clear exceptions when unscheduling
      weekSeries: this.weekSeries // Explicitly preserve weekSeries
    });
  }

  durationDisplay(): string {
    if (!this.timeStart || !this.timeEnd) return '';
    const duration = this.durationMinutes;
    if (duration === null) return '';
    return `${duration} min`;
  }
  
  // Time-related methods
  /**
   * Returns the time range in the format 'hh:mm - hh:mm'
   */
  timeRangeDisplay(): string {
    if (!this.timeStart || !this.timeEnd) return '';
    
    const formatTimeComponent = (timeString: string): string => {
      if (!timeString) return '';
      
      // If contains seconds (hh:mm:ss)
      if (timeString.includes(':')) {
        const parts = timeString.split(':');
        if (parts.length === 3) {
          return `${parts[0]}:${parts[1]}`;
        }
      }
      
      return timeString;
    };
    
    const formattedStart = formatTimeComponent(this.timeStart);
    const formattedEnd = formatTimeComponent(this.timeEnd);
    
    return `${formattedStart} - ${formattedEnd}`;
  }
  
  // For backwards compatibility
  get formattedTime(): string {
    return this.timeRangeDisplay();
  }
  
  get timeInfo(): any {
    const timeString = `${this.timeStart} - ${this.timeEnd}`;
    return parseTime(timeString);
  }
  
  get startMinutes(): number | null {
    if (!this.timeStart) return null;
    const parts = this.timeStart.split(':');
    if (parts.length < 2 || !parts[0] || !parts[1]) return null;
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) return null;
    
    return hours * 60 + minutes;
  }
  
  get endMinutes(): number | null {
    if (!this.timeEnd) return null;
    const parts = this.timeEnd.split(':');
    if (parts.length < 2 || !parts[0] || !parts[1]) return null;
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) return null;
    
    return hours * 60 + minutes;
  }

  /**
   * Parses the entry's time information into minutes from midnight.
   * @returns {{startMinutes: number|null, endMinutes: number|null, durationMinutes: number|null}} - Object containing start, end, and duration in minutes from midnight, or null if invalid.
   */
  parseTime(): TimeInfo {
    const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
    let startMinutes: number | null = null;
    let endMinutes: number | null = null;
    let durationMinutes: number | null = null;

    // Parse start time
    if (this.timeStart) {
      const startMatch = this.timeStart.match(timeRegex);
      if (startMatch && startMatch[1] && startMatch[2]) {
        const hours = parseInt(startMatch[1], 10);
        const minutes = parseInt(startMatch[2], 10);
        if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
          startMinutes = hours * 60 + minutes;
        }
      }
    }

    // Parse end time
    if (this.timeEnd) {
      const endMatch = this.timeEnd.match(timeRegex);
      if (endMatch && endMatch[1] && endMatch[2]) {
        const hours = parseInt(endMatch[1], 10);
        const minutes = parseInt(endMatch[2], 10);
        if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
          endMinutes = hours * 60 + minutes;
        }
      }
    }

    // Calculate duration if both start and end are valid
    if (startMinutes !== null && endMinutes !== null && endMinutes > startMinutes) {
      durationMinutes = endMinutes - startMinutes;
    }

    return { startMinutes, endMinutes, durationMinutes };
  }
  
  get durationMinutes(): number | null {
    const start = this.startMinutes;
    const end = this.endMinutes;
    
    if (start === null || end === null) return null;
    
    return end - start;
  }

  get startTime(): string {
    return this.startMinutes !== null ? formatTimeFromMinutes(this.startMinutes) : '';
  }
  
  get endTime(): string {
    return this.endMinutes !== null ? formatTimeFromMinutes(this.endMinutes) : '';
  }
  
  get displayName(): string {
    if (this.type === 'event') {
      return this.title || this.eventName || this.subject || '';
    } else {
      return this.subject || this.course || this.title || '';
    }
  }

  /**
   * Returns a concise, human-readable overview of the entry for tooltips.
   * Handles differences between 'event' and 'class'.
   * Lines are separated with newlines for native title tooltips.
   */
  getOverviewText(): string {
    const lines: string[] = [];

    // Common time label using normalized formatter
    const timeLabel = this.timeRangeDisplay();

    if (this.type === 'event') {
      const name = this.displayName;
      if (name) lines.push(name);

      // Prefer explicit host for events
      if (this.host) {
        lines.push(`Host: ${this.host}`);
      } else if (this.teachersFormatted) {
        lines.push(`Host: ${this.teachersFormatted}`);
      }

      if (this.location) {
        lines.push(`Location: ${this.location}`);
      }

      if (timeLabel) {
        lines.push(`Time: ${timeLabel}`);
      }
    } else {
      // class (lectures, etc.)
      const subjectName = this.subject || this.course || this.displayName;
      if (subjectName) lines.push(subjectName);

      const teachers = this.teachersFormatted || this.teacher;
      if (teachers) {
        lines.push(`Teachers: ${teachers}`);
      }

      const groups = this.groupsFormatted;
      if (groups) {
        // Format groups display: show all if <= 4, otherwise show first 3 + "x more"
        let groupsDisplay = groups;
        if (this.groups && this.groups.length > 4) {
          const groupNames = this.groups.map(group => {
            if (typeof group === 'object' && group.name) {
              return group.name;
            } else if (typeof group === 'string') {
              return group;
            } else if (typeof group === 'object' && group.group_code) {
              return group.group_code;
            }
            return '';
          }).filter(name => name);
          
          const firstThree = groupNames.slice(0, 3).join(', ');
          const remainingCount = groupNames.length - 3;
          groupsDisplay = `${firstThree} + ${remainingCount} more`;
        }
        lines.push(`Groups: ${groupsDisplay}`);
      }

      if (timeLabel) {
        lines.push(`Time: ${timeLabel}`);
      }
    }

    return lines.join('\n');
  }
  
  // For legacy code, update both timeStart and timeEnd
  set time(value: string) {
    if (!value) {
      this.timeStart = '';
      this.timeEnd = '';
      return;
    }
    
    const timeParts = value.split(' - ');
    if (timeParts.length === 2 && timeParts[0] && timeParts[1]) {
      this.timeStart = timeParts[0].trim();
      this.timeEnd = timeParts[1].trim();
    }
  }
  
  // Setter for weekSeries to re-parse when the property changes
  set weekSeries(value: string) {
    this._weekSeries = value;
    this._parsedWeekRanges = this._parseWeekSeries(value);
  }
  
  // Getter for weekSeries
  get weekSeries(): string {
    return this._weekSeries;
  }
  
  // Update the start time while preserving duration
  updateStartTime(newStartTimeMinutes: number): void {
    const { durationMinutes } = this.timeInfo;
    if (durationMinutes !== null) {
      const startTimeStr = formatTimeFromMinutes(newStartTimeMinutes);
      const endTimeStr = formatTimeFromMinutes(newStartTimeMinutes + durationMinutes);
      this.timeStart = startTimeStr;
      this.timeEnd = endTimeStr;
    }
  }
  
  // Update the time with new start and end times
  updateTimeRange(newStartTimeStr: string, newEndTimeStr: string): void {
    if (newStartTimeStr && newEndTimeStr) {
      this.timeStart = newStartTimeStr;
      this.timeEnd = newEndTimeStr;
    }
  }
  
  // Convert to plain object (for API calls, etc.)
  toObject(): ScheduleEntryData & { time?: string } {
    const subjRef = this.subjectObj || undefined;
    const teachRef = this.teacherObj || undefined;
    const roomRef = this.roomObj || undefined;

    const obj: ScheduleEntryData & { time?: string } = {
      id: this.id,
      subject: (subjRef as any) ?? null,
      course: this.course, // Keep for backward compatibility
      type: this.type,
      teacher: (teachRef as any) ?? null, // Legacy single teacher
      teachers: this.teachersArray, // NEW: Multiple teachers array
      host: this.host, // Host for events
      groups: this.groups,
      room: (roomRef as any) ?? null,
      day: this.day,
      weekSeries: this.weekSeries,
      year: this.year,
      timeStart: this.timeStart,
      timeEnd: this.timeEnd,
      publishIdentity: this.publishIdentity,
      // Convenience display fields for consumers that diff plain objects
      displayName: this.displayName,
      formattedTime: this.formattedTime,
      teachersFormatted: this.teachersFormatted,
      notes: this.notes,
      exceptions: this.exceptions,
      periodId: this.periodId,
      title: this.title,
      eventName: this.eventName,
      description: this.description,
      location: this.location,
      // IDs preferred for API calls (derived from association objects)
      teacher_id: (this.teacherObj as any)?.id,
      subject_id: (this.subjectObj as any)?.id,
      room_id: (this.roomObj as any)?.id,
      // Updated conflict status properties for per-week conflicts
      conflictsByWeek: this.conflictsByWeek,
      conflictLastCalculated: this.conflictLastCalculated,
      ...this.additionalProps
    };

    // Only include time field if both timeStart and timeEnd are valid
    if (this.timeStart && this.timeEnd) {
      obj.time = `${this.timeStart} - ${this.timeEnd}`;
    }

    // For events, ensure proper field mapping and exclude teacher fields
    if (this.type === 'event') {
      // Use title as the primary name field for events
      if (!obj.title && this.eventName) {
        obj.title = this.eventName;
      }
      // Events shouldn't have subject field
      if (!this.subject) {
        obj.subject = '';
        obj.course = ''; // Backward compatibility
      }
      // Events should not have teacher fields to avoid backend validation errors
      obj.teacher = undefined;
      obj.teachers = [];
      obj.teacher_id = undefined;
      delete obj.teacher_id;
    }

    return obj;
  }
  
  // Create a copy of the entry
  clone(): ScheduleEntry {
    return new ScheduleEntry(this.toObject());
  }
  
  // Create a ScheduleEntry from a plain object
  static fromObject(data: ScheduleEntryData): ScheduleEntry {
    return new ScheduleEntry(data);
  }
  
  // Create an array of ScheduleEntry objects from an array of plain objects
  static fromArray(dataArray: ScheduleEntryData[]): ScheduleEntry[] {
    return Array.isArray(dataArray) 
      ? dataArray.map(data => new ScheduleEntry(data))
      : [];
  }

  /**
   * Checks if this schedule entry is active for the specified week number and year.
   * Handles week ranges, comma-separated weeks, and cross-year ranges.
   * Now properly accounts for academic year boundaries when mapping weeks to calendar years.
   * @param {number} targetWeekNumber - The ISO week number to check (1-53)
   * @param {number} targetYear - The year to check
   * @returns {boolean} - True if the entry is active for the specified week, false otherwise
   */
  async isActiveForWeek(targetWeekNumber: number, targetYear: number): Promise<boolean> {
    // If no week series is specified, consider it active for all weeks
    if (!this._parsedWeekRanges) {
      return true;
    }

    const academicYearService = AcademicYearService.getInstance();
    
    try {
      // Get academic year structure to properly map weeks to calendar years
      const academicStructure = await academicYearService.getAcademicYearStructure();
      
      if (!academicStructure) {
        // Fallback to old behavior if no academic structure available
        return this.isActiveForWeekFallback(targetWeekNumber, targetYear);
      }

      // Check each parsed week range against the target week/year
      for (const range of this._parsedWeekRanges) {
        if (range.type === 'single') {
          // Single week number - map to correct calendar year
          const weekCalendarYear = AcademicYearService.mapWeekToCalendarYear(
            range.week!,
            academicStructure.academicYear,
            academicStructure.startWeek,
            academicStructure.endWeek
          );
          
          if (targetYear === weekCalendarYear && targetWeekNumber === range.week) {
            return true;
          }
        } else if (range.type === 'range') {
          // Week range - check if target week falls within the range
          if (range.crossYear) {
            // Cross-year range like "52-4" (week 52 to week 4 of next year)
            const startCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.start!,
              academicStructure.academicYear,
              academicStructure.startWeek,
              academicStructure.endWeek
            );
            const endCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.end!,
              academicStructure.academicYear,
              academicStructure.startWeek,
              academicStructure.endWeek
            );

            // Check if target week is in the start range
            if (targetYear === startCalendarYear && targetWeekNumber >= range.start! && targetWeekNumber <= 53) {
              return true;
            }
            // Check if target week is in the end range
            if (targetYear === endCalendarYear && targetWeekNumber >= 1 && targetWeekNumber <= range.end!) {
              return true;
            }
          } else {
            // Regular range within the same year
            const startCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.start!,
              academicStructure.academicYear,
              academicStructure.startWeek,
              academicStructure.endWeek
            );
            const endCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.end!,
              academicStructure.academicYear,
              academicStructure.startWeek,
              academicStructure.endWeek
            );

            // For regular ranges, start and end should be in the same calendar year
            if (startCalendarYear === endCalendarYear && targetYear === startCalendarYear) {
              if (targetWeekNumber >= range.start! && targetWeekNumber <= range.end!) {
                return true;
              }
            }
          }
        }
      }
      
      // Removed debug logging for performance
      return false;

    } catch (error) {
      console.error(`[ScheduleEntry ${this.id}] Error checking week activity:`, error);
      // Fallback to old behavior on error
      return this.isActiveForWeekFallback(targetWeekNumber, targetYear);
    }
  }

  /**
   * Fallback method for checking week activity when academic year structure is not available.
   * Uses the old logic for backward compatibility.
   * @param {number} targetWeekNumber - The ISO week number to check (1-53)
   * @param {number} targetYear - The year to check
   * @returns {boolean} - True if the entry is active for the specified week, false otherwise
   * @private
   */
  private isActiveForWeekFallback(targetWeekNumber: number, targetYear: number): boolean {
    if (!this._parsedWeekRanges) {
      return true;
    }

    const entryYear = this.year;
    
    for (const range of this._parsedWeekRanges) {
      if (range.type === 'single') {
        // Single week number
        if (targetYear === entryYear && targetWeekNumber === range.week) {
          return true;
        }
      } else if (range.type === 'range') {
        if (range.crossYear) {
          // Cross-year range like "52-4" (week 52 of current year to week 4 of next year)
          // If target year matches entry year and target week is in the start range
          if (targetYear === entryYear && targetWeekNumber >= range.start! && targetWeekNumber <= 53) {
            return true;
          }
          // If target year is the year after entry year and target week is in the end range
          if (targetYear === entryYear + 1 && targetWeekNumber >= 1 && targetWeekNumber <= range.end!) {
            return true;
          }
        } else {
          // Regular range within the same year
          if (targetYear === entryYear && targetWeekNumber >= range.start! && targetWeekNumber <= range.end!) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  /**
   * Synchronous version of isActiveForWeek for backward compatibility.
   * Uses cached academic year data if available, otherwise falls back to old logic.
   * @param {number} targetWeekNumber - The ISO week number to check (1-53)
   * @param {number} targetYear - The year to check
   * @returns {boolean} - True if the entry is active for the specified week, false otherwise
   */
  isActiveForWeekSync(targetWeekNumber: number, targetYear: number): boolean {
    const academicYearService = AcademicYearService.getInstance();
    const cachedStructure = (academicYearService as any).cachedStructure;
    
    if (cachedStructure && this._parsedWeekRanges) {
      // Use cached academic structure for synchronous checking
      for (const range of this._parsedWeekRanges) {
        if (range.type === 'single') {
          const weekCalendarYear = AcademicYearService.mapWeekToCalendarYear(
            range.week!,
            cachedStructure.academicYear,
            cachedStructure.startWeek,
            cachedStructure.endWeek
          );
          
          if (targetYear === weekCalendarYear && targetWeekNumber === range.week) {
            return true;
          }
        } else if (range.type === 'range') {
          if (range.crossYear) {
            const startCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.start!,
              cachedStructure.academicYear,
              cachedStructure.startWeek,
              cachedStructure.endWeek
            );
            const endCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.end!,
              cachedStructure.academicYear,
              cachedStructure.startWeek,
              cachedStructure.endWeek
            );

            if (targetYear === startCalendarYear && targetWeekNumber >= range.start! && targetWeekNumber <= 53) {
              return true;
            }
            if (targetYear === endCalendarYear && targetWeekNumber >= 1 && targetWeekNumber <= range.end!) {
              return true;
            }
          } else {
            const startCalendarYear = AcademicYearService.mapWeekToCalendarYear(
              range.start!,
              cachedStructure.academicYear,
              cachedStructure.startWeek,
              cachedStructure.endWeek
            );
            
            if (targetYear === startCalendarYear && targetWeekNumber >= range.start! && targetWeekNumber <= range.end!) {
              return true;
            }
          }
        }
      }
      return false;
    }
    
    // Fallback to old logic if no cached structure
    return this.isActiveForWeekFallback(targetWeekNumber, targetYear);
  }

  /**
   * Removes a specific week from this schedule entry's week series.
   * @param {number} weekNumber - The week number to remove (1-53)
   * @param {number} targetYear - The year context for the week removal
   * @returns {ScheduleEntry|null} - A new ScheduleEntry with the updated week series, or null if no weeks remain
   */
  removeWeek(weekNumber: number, targetYear: number | null = null): ScheduleEntry | null {
    const targetWeekNumber = parseInt(weekNumber.toString(), 10);
    const checkYear = targetYear || this.year;
    
    // If this entry is not active for the target week, nothing to remove
    if (!this.isActiveForWeek(targetWeekNumber, checkYear)) {
      console.warn('[ScheduleEntry] Week', weekNumber, 'is not active for this entry');
      return this; // Return unchanged entry
    }

    // If no parsed ranges (meaning active for all weeks), we can't remove a specific week
    if (!this._parsedWeekRanges) {
      console.warn('[ScheduleEntry] Cannot remove week from entry with no specific week series');
      return this; // Return unchanged entry
    }

    // Build new week series by reconstructing from parsed ranges, excluding the target week
    const newWeekParts: string[] = [];
    
    for (const range of this._parsedWeekRanges) {
      if (range.type === 'single') {
        // Keep single weeks that don't match the target
        if (range.week !== targetWeekNumber) {
          newWeekParts.push(range.week!.toString());
        }
      } else if (range.type === 'range') {
        if (range.crossYear) {
          // Handle cross-year ranges like "52-4"
          const weekInStartRange = targetWeekNumber >= range.start! && targetWeekNumber <= 53;
          const weekInEndRange = targetWeekNumber >= 1 && targetWeekNumber <= range.end!;
          
          if (weekInStartRange) {
            // Remove from start range
            if (range.start === targetWeekNumber && range.start === 53) {
              // Only week 53 in start range, remove it entirely, keep end range
              if (range.end! > 1) {
                newWeekParts.push(`1-${range.end}`);
              } else {
                newWeekParts.push('1');
              }
            } else if (range.start === targetWeekNumber) {
              // Remove first week of start range
              newWeekParts.push(`${range.start! + 1}-${range.end}`);
            } else if (targetWeekNumber === 53) {
              // Remove last week of start range
              if (range.start === 52) {
                newWeekParts.push(`1-${range.end}`);
              } else {
                newWeekParts.push(`${range.start}-52,1-${range.end}`);
              }
            } else {
              // Split the range
              newWeekParts.push(`${range.start}-${targetWeekNumber - 1}`, `${targetWeekNumber + 1}-${range.end}`);
            }
          } else if (weekInEndRange) {
            // Remove from end range
            if (range.end === targetWeekNumber && range.end === 1) {
              // Only week 1 in end range, keep start range only
              newWeekParts.push(`${range.start}-53`);
            } else if (range.end === targetWeekNumber) {
              // Remove last week of end range
              newWeekParts.push(`${range.start}-${range.end! - 1}`);
            } else if (targetWeekNumber === 1) {
              // Remove first week of end range
              if (range.end === 2) {
                newWeekParts.push(`${range.start}-53`);
              } else {
                newWeekParts.push(`${range.start}-53,2-${range.end}`);
              }
            } else {
              // Split the range
              newWeekParts.push(`${range.start}-53`, `1-${targetWeekNumber - 1}`, `${targetWeekNumber + 1}-${range.end}`);
            }
          } else {
            // Week not in this cross-year range, keep it
            newWeekParts.push(`${range.start}-${range.end}`);
          }
        } else {
          // Regular range within same year
          if (targetWeekNumber >= range.start! && targetWeekNumber <= range.end!) {
            // Week is in this range, need to split or modify
            if (range.start === range.end) {
              // Single week range, remove entirely
              continue;
            } else if (targetWeekNumber === range.start) {
              // Remove first week of range
              if (range.start! + 1 <= range.end!) {
                newWeekParts.push(`${range.start! + 1}-${range.end}`);
              }
            } else if (targetWeekNumber === range.end) {
              // Remove last week of range
              if (range.start! <= range.end! - 1) {
                newWeekParts.push(`${range.start}-${range.end! - 1}`);
              }
            } else {
              // Split the range
              newWeekParts.push(`${range.start}-${targetWeekNumber - 1}`, `${targetWeekNumber + 1}-${range.end}`);
            }
          } else {
            // Week not in this range, keep the range
            newWeekParts.push(`${range.start}-${range.end}`);
          }
        }
      }
    }

    if (newWeekParts.length === 0) {
      // No weeks left
      return null;
    } else {
      // Create new entry with updated week series
      return new ScheduleEntry({
        ...this.toObject(),
        weekSeries: newWeekParts.join(',')
      });
    }
  }

  /**
   * Finds an exception for a specific week and year.
   * @param {number} weekNumber - The week number.
   * @param {number} year - The year.
   * @returns {ScheduleEntryException | undefined} The exception object or undefined if not found.
   */
  public getExceptionForWeek(weekNumber: number, year: number): ScheduleEntryException | undefined {
    if (!this.exceptions || !Array.isArray(this.exceptions)) {
      return undefined;
    }
    return this.exceptions.find(ex => ex.week === weekNumber && ex.year === year);
  }

  /**
   * Returns a new ScheduleEntry instance with exceptions for a specific week and year applied.
   * If the entry is cancelled for that week, it returns null.
   * If no exception is found, it returns the original entry.
   * The new entry will have a modified ID and a flag to indicate it's an exception.
   * @param {number} weekNumber - The week number to check for exceptions.
   * @param {number} year - The year to check for exceptions.
   * @returns {ScheduleEntry | null} A new ScheduleEntry instance with applied exceptions, null if cancelled, or the original entry.
   */
  public getEntryForWeek(weekNumber: number, year: number): ScheduleEntry | null {
    const exception = this.getExceptionForWeek(weekNumber, year);

    if (exception) {
      if (exception.changes.cancelled) {
        return null; // The entry is cancelled for this week.
      }

      // Clone original data and normalize exception changes to use consistent field names
      const entryData = this.toObject();
      const normalizedExceptionChanges = normalizeScheduleEntryData(exception.changes);
      
      const overriddenData: ScheduleEntryData = {
        ...entryData,
        // Apply normalized exception changes to avoid field name conflicts
        ...normalizedExceptionChanges,
        // Create a unique ID for this specific occurrence
        id: `${entryData.id}-w${weekNumber}y${year}`,
        // Add custom fields to identify this as a special instance
        additionalProps: {
          ...this.additionalProps,
          isException: true,
          originalId: this.id,
          exceptionWeek: weekNumber,
          exceptionYear: year,
        }
      };

      // Remove legacy time field to avoid conflicts with exception timeStart/timeEnd
      if (normalizedExceptionChanges.timeStart || normalizedExceptionChanges.timeEnd) {
        delete overriddenData.time;
      }

      // Backward-compat: If the exception specifies legacy "teacher" but not "teachers",
      // construct a single-entry teachers array so UI reflects it.
      if (
        (normalizedExceptionChanges as any).teacher !== undefined &&
        (normalizedExceptionChanges as any).teachers === undefined
      ) {
        const teacherValue: any = (normalizedExceptionChanges as any).teacher;
        const teacherName = typeof teacherValue === 'object' && teacherValue !== null
          ? (teacherValue.name || '')
          : String(teacherValue ?? '');

        if (teacherName) {
          overriddenData.teachers = [
            {
              id: (teacherValue && typeof teacherValue === 'object' && (teacherValue as any).id) ? (teacherValue as any).id : ((overriddenData as any).teacher_id || 0),
              name: teacherName,
              teacher_code: '',
              ScheduleEntryTeachers: { teacher_order: 1, role: 'primary' }
            } as any
          ];
        }
      }

      // DO NOT override weekSeries - keep the original weekSeries so editing works correctly
      // The filtering by week should happen separately in the display logic, not here

      return new ScheduleEntry(overriddenData);
    }

    return this;
  }

  /**
   * Adds or updates an exception for a specific week and year, returning a new ScheduleEntry instance.
   * @param {ScheduleEntryException} exception - The exception to add or update.
   * @returns {ScheduleEntry} A new ScheduleEntry instance with the updated exceptions.
   */
  public addException(exception: ScheduleEntryException): ScheduleEntry {
    // Clone the current entry data
    const entryData = this.toObject();
    
    // Initialize exceptions array if it doesn't exist
    const currentExceptions = Array.isArray(this.exceptions) ? [...this.exceptions] : [];
    
    // Find existing exception for the same week and year
    const existingIndex = currentExceptions.findIndex(
      ex => ex.week === exception.week && ex.year === exception.year
    );
    
    if (existingIndex > -1) {
      // Update existing exception by merging changes
      const existingException = currentExceptions[existingIndex];
      if (existingException) {
        currentExceptions[existingIndex] = {
          week: existingException.week, // Explicitly carry over week
          year: existingException.year, // Explicitly carry over year
          changes: {
            ...(existingException.changes || {}), // Defensively handle if changes is missing
            ...exception.changes
          }
        };
      }
    } else {
      // Add new exception
      currentExceptions.push(exception);
    }
    
    // Create new ScheduleEntry with updated exceptions
    return new ScheduleEntry({
      ...entryData,
      exceptions: currentExceptions
    });
  }

  // Conflict status methods
  /**
   * Sets the conflict status for a specific week
   */
  setConflictStatusForWeek(
    weekNumber: number, 
    year: number,
    status: 'green' | 'orange' | 'red', 
    reasons: Array<{ reason: string; severity: 'green' | 'orange' | 'red' }> = []
  ): void {
    const key = `${weekNumber}-${year}`;
    this.conflictsByWeek.set(key, {
      week: weekNumber,
      year: year,
      status: status,
      reasons: reasons,
      lastCalculated: new Date()
    });
    this.conflictLastCalculated = new Date();
  }

  /**
   * Gets the conflict status for a specific week
   */
  getConflictStatusForWeek(weekNumber: number, year: number): WeekConflictInfo | null {
    const key = `${weekNumber}-${year}`;
    return this.conflictsByWeek.get(key) || null;
  }

  /**
   * Gets the overall conflict status (most severe across all weeks)
   */
  getOverallConflictStatus(): 'green' | 'orange' | 'red' {
    if (this.conflictsByWeek.size === 0) return 'green';
    
    let hasRed = false;
    let hasOrange = false;
    
    this.conflictsByWeek.forEach((conflictInfo, key) => {
      if (conflictInfo.status === 'red') {
        hasRed = true;
      } else if (conflictInfo.status === 'orange') {
        hasOrange = true;
      }
    });
    
    if (hasRed) return 'red';
    if (hasOrange) return 'orange';
    return 'green';
  }

  /**
   * Clears the conflict status for a specific week
   */
  clearConflictStatusForWeek(weekNumber: number, year: number): void {
    const key = `${weekNumber}-${year}`;
    this.conflictsByWeek.delete(key);
    this.conflictLastCalculated = new Date();
  }

  /**
   * Clears all conflict statuses
   */
  clearAllConflictStatuses(): void {
    this.conflictsByWeek.clear();
    this.conflictLastCalculated = new Date();
  }

  /**
   * Legacy method for backward compatibility - sets conflict for all active weeks
   */
  setConflictStatus(status: 'green' | 'orange' | 'red', reasons: Array<{ reason: string; severity: 'green' | 'orange' | 'red' }> = []): void {
    console.warn('[ScheduleEntry] setConflictStatus is deprecated, use setConflictStatusForWeek instead');
    this.clearAllConflictStatuses();
    // Set conflict for current year, week 1 as fallback
    this.setConflictStatusForWeek(1, new Date().getFullYear(), status, reasons);
  }

  /**
   * Legacy method for backward compatibility
   */
  clearConflictStatus(): void {
    console.warn('[ScheduleEntry] clearConflictStatus is deprecated, use clearAllConflictStatuses instead');
    this.clearAllConflictStatuses();
  }

  /**
   * Gets the most severe conflict reason across all weeks
   */
  getConflictSummary(): string {
    if (this.conflictsByWeek.size === 0) return '';
    
    // Get all conflict reasons and find the most severe
    const allReasons: Array<{ reason: string; severity: 'green' | 'orange' | 'red' }> = [];
    
    this.conflictsByWeek.forEach((conflictInfo, key) => {
      allReasons.push(...conflictInfo.reasons);
    });
    
    const redConflicts = allReasons.filter(r => r.severity === 'red');
    const orangeConflicts = allReasons.filter(r => r.severity === 'orange');
    
    if (redConflicts.length > 0) {
      return redConflicts[0]?.reason || '';
    } else if (orangeConflicts.length > 0) {
      return orangeConflicts[0]?.reason || '';
    }
    return allReasons[0]?.reason || '';
  }

  /**
   * Gets the conflict summary for a specific week
   */
  getConflictSummaryForWeek(weekNumber: number, year: number): string {
    const conflictInfo = this.getConflictStatusForWeek(weekNumber, year);
    if (!conflictInfo || conflictInfo.reasons.length === 0) return '';
    
    const redConflicts = conflictInfo.reasons.filter(r => r.severity === 'red');
    const orangeConflicts = conflictInfo.reasons.filter(r => r.severity === 'orange');
    
    if (redConflicts.length > 0) {
      return redConflicts[0]?.reason || '';
    } else if (orangeConflicts.length > 0) {
      return orangeConflicts[0]?.reason || '';
    }
    return conflictInfo.reasons[0]?.reason || '';
  }

  /**
   * Gets the conflict icon based on overall status
   */
  getConflictIcon(): string | null {
    const status = this.getOverallConflictStatus();
    switch (status) {
      case 'red': return '❗';
      case 'orange': return '⚠️';
      default: return null;
    }
  }

  /**
   * Gets the conflict icon for a specific week
   */
  getConflictIconForWeek(weekNumber: number, year: number): string | null {
    const conflictInfo = this.getConflictStatusForWeek(weekNumber, year);
    if (!conflictInfo) return null;
    
    switch (conflictInfo.status) {
      case 'red': return '❗';
      case 'orange': return '⚠️';
      default: return null;
    }
  }

  /**
   * Gets all weeks where this entry is active within a given year range
   * This is used for conflict checking
   */
  async getActiveWeeks(startYear: number, endYear: number): Promise<Array<{week: number, year: number}>> {
    const activeWeeks: Array<{week: number, year: number}> = [];
    
    // If no week series specified, return empty array (we can't determine active weeks)
    if (!this._parsedWeekRanges) {
      return activeWeeks;
    }

    for (let year = startYear; year <= endYear; year++) {
      for (let week = 1; week <= 53; week++) {
        if (await this.isActiveForWeek(week, year)) {
          activeWeeks.push({ week, year });
        }
      }
    }

    return activeWeeks;
  }

  /**
   * Synchronous version of getActiveWeeks using cached data
   */
  getActiveWeeksSync(startYear: number, endYear: number): Array<{week: number, year: number}> {
    const activeWeeks: Array<{week: number, year: number}> = [];
    
    // If no week series specified, return empty array (we can't determine active weeks)
    if (!this._parsedWeekRanges) {
      return activeWeeks;
    }

    for (let year = startYear; year <= endYear; year++) {
      for (let week = 1; week <= 53; week++) {
        if (this.isActiveForWeekSync(week, year)) {
          activeWeeks.push({ week, year });
        }
      }
    }

    return activeWeeks;
  }

  /**
   * Check if this entry overlaps with another entry in terms of week series, day, and time
   */
  hasConflictPotentialWith(otherEntry: ScheduleEntry): boolean {
    // Must be on the same day
    if (this.day !== otherEntry.day) {
      return false;
    }

    // Must have valid time information
    if (!this.isTimeValid || !otherEntry.isTimeValid) {
      return false;
    }

    // Check for time overlap
    const thisTimeInfo = this.parseTime();
    const otherTimeInfo = otherEntry.parseTime();
    
    if (thisTimeInfo.startMinutes === null || thisTimeInfo.endMinutes === null ||
        otherTimeInfo.startMinutes === null || otherTimeInfo.endMinutes === null) {
      return false;
    }

    // Check if time ranges overlap
    const timeOverlap = thisTimeInfo.startMinutes < otherTimeInfo.endMinutes && 
                       thisTimeInfo.endMinutes > otherTimeInfo.startMinutes;
    
    if (!timeOverlap) {
      return false;
    }

    // If we get here, they overlap in day and time, so they have conflict potential
    return true;
  }

  /**
   * Check if this entry should be included when filtering by groups.
   * For events with empty groups, returns true (meaning "All Groups").
   * For classes, checks if any of the specified groups match this entry's groups.
   * @param {string[]} selectedGroups - Array of group identifiers to check against
   * @returns {boolean} - True if this entry should be included in the group filter
   */
  includesGroup(selectedGroups: string[]): boolean {
    // If no groups are selected, include all entries
    if (!selectedGroups || selectedGroups.length === 0) {
      return true;
    }

    if (selectedGroups.some((g) => String(g).trim() === SCHEDULE_FILTER_ALL_GROUPS)) {
      return true;
    }

    // For events with empty groups, they are visible to "All Groups"
    if (this.type === 'event' && (!this.groups || this.groups.length === 0)) {
      return true;
    }

    // For classes or events with specific groups, check if any group matches
    if (!this.groups || this.groups.length === 0) {
      return false;
    }

    // Check if any of this entry's groups match any of the selected groups
    return this.groups.some(entryGroup => {
      const groupIdentifier = typeof entryGroup === 'object' 
        ? (entryGroup.name || entryGroup.group_code || entryGroup.id) 
        : entryGroup;
      return selectedGroups.includes(String(groupIdentifier));
    });
  }
}

export default ScheduleEntry;
export type { ScheduleEntryData, NormalizedScheduleEntryData, ParsedWeekRange, TimeInfo, ScheduleEntryException, WeekConflictInfo, TeacherAssociation };
export { normalizeScheduleEntryData };

/* 
TESTING EXAMPLES to verify the fixes:

// Test 1: Exception override bug fix
const originalEntry = new ScheduleEntry({
  id: 'test',
  timeStart: '09:00',
  timeEnd: '10:30',
  type: 'class',
  teacher: 'John Doe'
});

const exception = {
  week: 10,
  year: 2024,
  changes: {
    room: 'Room B'  // Only room is specified, no type field
  }
};

const entryWithException = originalEntry.addException(exception);
const weekEntry = entryWithException.getEntryForWeek(10, 2024);

// EXPECTED RESULT:
// weekEntry.room should be 'Room B' (from exception)
// weekEntry.type should be 'class' (from original, NOT overridden with empty string)
// weekEntry.teacher should be 'John Doe' (from original)

// Test 2: WeekSeries preservation for editing
const originalEntryWithWeeks = new ScheduleEntry({
  id: 'test2',
  weekSeries: '38,40,42-45',
  timeStart: '10:00',
  timeEnd: '11:30',
  type: 'class',
  teacher: 'Jane Smith',
  year: 2024
});

const timeException = {
  week: 40,
  year: 2024,
  changes: {
    timeStart: '11:00',
    timeEnd: '12:30'
  }
};

const entryWithTimeException = originalEntryWithWeeks.addException(timeException);
const week40Entry = entryWithTimeException.getEntryForWeek(40, 2024);

// EXPECTED RESULT (FIXED):
// week40Entry.weekSeries should be '38,40,42-45' (original weeks preserved for editing)
// week40Entry.timeStart should be '11:00' (from exception)
// week40Entry.timeEnd should be '12:30' (from exception)
// week40Entry.teacher should be 'Jane Smith' (from original)

console.log('Week 40 Entry weekSeries:', week40Entry.weekSeries); // Should be '38,40,42-45'
console.log('Week 40 Entry time:', week40Entry.timeStart, '-', week40Entry.timeEnd); // Should be '11:00 - 12:30'
*/ 